import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  writeBatch,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { notificationsFromSnapshot } from '../utils/notificationUtils';

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return [];
      }

      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
      );

      const snapshot = await getDocs(notificationsQuery);
      return notificationsFromSnapshot(snapshot);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return rejectWithValue(error.message);
    }
  },
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markNotificationAsRead',
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        isRead: true,
        readAt: serverTimestamp(),
      });
      return notificationId;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return rejectWithValue(error.message);
    }
  },
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllNotificationsAsRead',
  async (userId, { rejectWithValue }) => {
    try {
      const unreadQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('isRead', '==', false),
      );
      const snapshot = await getDocs(unreadQuery);

      const batch = writeBatch(db);
      snapshot.docs.forEach(notificationDoc => {
        batch.update(notificationDoc.ref, {
          isRead: true,
          readAt: serverTimestamp(),
        });
      });

      await batch.commit();
      return snapshot.docs.map(doc => doc.id);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return rejectWithValue(error.message);
    }
  },
);

export const addNotification = createAsyncThunk(
  'notifications/addNotification',
  async (notification, { rejectWithValue }) => {
    try {
      const payload = {
        ...notification,
        createdAt: serverTimestamp(),
        isRead: false,
      };
      const docRef = await addDoc(collection(db, 'notifications'), payload);
      return {
        id: docRef.id,
        ...notification,
        createdAt: Date.now(),
        isRead: false,
      };
    } catch (error) {
      console.error('Error adding notification:', error);
      return rejectWithValue(error.message);
    }
  },
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotifications: state => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    setNotificationsFromListener: (state, action) => {
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter(n => !n.isRead).length;
      state.loading = false;
    },
    setNotificationsLoading: (state, action) => {
      state.loading = action.payload;
    },
    addNotificationToState: (state, action) => {
      const exists = state.notifications.some(n => n.id === action.payload.id);
      if (!exists) {
        state.notifications.unshift(action.payload);
        state.unreadCount = state.notifications.filter(n => !n.isRead).length;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNotifications.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
        state.unreadCount = action.payload.filter(n => !n.isRead).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(
          n => n.id === action.payload,
        );
        if (notification) {
          notification.isRead = true;
          notification.readAt = Date.now();
          state.unreadCount = state.notifications.filter(n => !n.isRead).length;
        }
      })
      .addCase(markAllNotificationsAsRead.fulfilled, state => {
        state.notifications = state.notifications.map(notification => ({
          ...notification,
          isRead: true,
          readAt: Date.now(),
        }));
        state.unreadCount = 0;
      })
      .addCase(addNotification.fulfilled, (state, action) => {
        state.notifications.unshift(action.payload);
        state.unreadCount = state.notifications.filter(n => !n.isRead).length;
      });
  },
});

export const {
  clearNotifications,
  setNotificationsFromListener,
  setNotificationsLoading,
  addNotificationToState,
} = notificationsSlice.actions;

export const selectNotifications = state => state.notifications.notifications;
export const selectUnreadCount = state => state.notifications.unreadCount;
export const selectNotificationsLoading = state => state.notifications.loading;

export default notificationsSlice.reducer;


